import { useState, useEffect, useMemo } from "react";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import SearchField from "@components/ui/textfields/SearchField";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { projectsApi } from "@services/api/projects";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";

type Record = {
  objectID: string;
  org_id?: string;
  name?: string;
  display_name?: string;
  num?: string;
  city?: string;
  status?: string;
  accessible_user_ids?: string[];
  team?: { conductor_id?: string; commercial_id?: string; sav_id?: string; comptability_id?: string };
  [key: string]: any;
};

export const AlgoliaRecords = () => {
  const [records, setRecords] = useState<Record[]>([]);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const userData = useDataStore((state) => state.userData);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const { records } = await projectsApi.listAlgoliaRecords();
      setRecords(records as Record[]);
    } catch (error: any) {
      console.error("Failed to fetch Algolia records:", error);
      showNotification({ type: "error", message: "Impossible de charger les enregistrements Algolia" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleResync = async () => {
    setResyncing(true);
    try {
      const result = await projectsApi.syncAllAlgolia();
      showNotification({
        type: "success",
        message: `Sync OK — ${result.indexed} indexés, ${result.errors} erreurs`,
      });
      await fetchRecords();
    } catch (error: any) {
      console.error("Resync failed:", error);
      showNotification({ type: "error", message: "La resynchronisation a échoué" });
    } finally {
      setResyncing(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) => {
      const haystack = [r.name, r.display_name, r.num, r.city, r.objectID]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (haystack.includes(q)) return true;
      if ((r.accessible_user_ids || []).some((uid) => uid.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [records, search]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex flex-col w-full max-w-1p5k px-4 lg:px-10 py-10 gap-4">
      <div className="flex flex-row items-center justify-between gap-3">
        <div className="flex flex-col">
          <Text1>Algolia — enregistrements</Text1>
          <Text2>
            {`Vue debug du contenu indexé pour ${userData?.org_id ?? "?"} (${records.length} enregistrement${records.length > 1 ? "s" : ""}).`}
          </Text2>
          <Text3>
            {"Vérifie que accessible_user_ids contient bien les UIDs équipe + visible_member_ids + guest_user_ids."}
          </Text3>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <SecondaryButton onClick={fetchRecords} disabled={loading || resyncing}>
            Rafraîchir
          </SecondaryButton>
          <Button
            label={resyncing ? "Resync en cours..." : "Resync complet"}
            onClick={handleResync}
            disabled={loading || resyncing}
          />
        </div>
      </div>

      <SearchField
        value={search}
        onChange={setSearch}
        placeholder="Rechercher par nom, num, ville, ID Firestore ou UID..."
      />

      {loading ? (
        <div className="flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 rounded-lg bg-neutral-100 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-h3 text-neutral-400 py-8 text-center">
          Aucun enregistrement{search ? " pour cette recherche" : ""}.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((r) => {
            const isOpen = expanded.has(r.objectID);
            return (
              <div key={r.objectID} className="border rounded-lg bg-white">
                <button
                  className="w-full flex flex-row items-center justify-between px-4 py-3 hover:bg-neutral-50"
                  onClick={() => toggleExpand(r.objectID)}
                >
                  <div className="flex flex-col items-start text-left">
                    <span className="text-h3 font-medium">
                      {r.display_name || r.name || "(sans nom)"}{" "}
                      <span className="text-neutral-400">— {r.num || r.objectID}</span>
                    </span>
                    <span className="text-h4 text-neutral-500">
                      {r.city || ""} · {(r.accessible_user_ids || []).length} UID accessibles · status: {r.status || "?"}
                    </span>
                  </div>
                  <span className="text-neutral-400">{isOpen ? "▾" : "▸"}</span>
                </button>
                {isOpen && (
                  <div className="px-4 py-3 border-t bg-neutral-50">
                    <pre className="text-h5 whitespace-pre-wrap break-all font-mono">
                      {JSON.stringify(r, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
